import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { Grid, Typography } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import "./Admin.css";
import { saveConferenceInfo, validateAddress1, validateAddress2, validateCity, validateEmail, validateImage, validateName, validatePhone, validateState, validateZip } from "../../services/ConferenceInformation";
import Alert from "@mui/material/Alert";
import CheckIcon from "@mui/icons-material/Check";

export default function ConferenceDetails({conferenceInfo, onConferenceInfoUpdate }: { conferenceInfo: any, onConferenceInfoUpdate: () => void }) {
  // State variable to hold the conference information
  const [conferenceInfoDataUI, setConferenceInfoDataUI] = useState(conferenceInfo);

  // State variables to store error messages for each field
  const [nameError, setNameError] = useState<string | null>(null);
  const [address1Error, setAddress1Error] = useState<string | null>(null);
  const [address2Error, setAddress2Error] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [cityError, setCityError] = useState<string | null>(null);
  const [stateError, setStateError] = useState<string | null>(null);
  const [zipError, setZipError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isShowAlert, setShowAlert] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    setConferenceInfoDataUI(conferenceInfo);
  }, [conferenceInfo]);

  useEffect(() => {
    const currentData = JSON.stringify(conferenceInfoDataUI);
    const originalData = JSON.stringify(conferenceInfo);

    setHasUnsavedChanges(currentData !== originalData);
  }, [conferenceInfoDataUI, conferenceInfo]);



  //handle save (Button Save)
  const handleSave = async () => {
    // Validate conferenceInfoDataUI instead of conferenceInfo
    const isValid =
      validateName(conferenceInfoDataUI.Name) &&
      validateAddress1(conferenceInfoDataUI.Address1) &&
      validateAddress2(conferenceInfoDataUI.Address2) &&
      validatePhone(conferenceInfoDataUI.Phone) &&
      validateEmail(conferenceInfoDataUI.Email) &&
      validateCity(conferenceInfoDataUI.City) &&
      validateState(conferenceInfoDataUI.State) &&
      validateZip(conferenceInfoDataUI.Zip) &&
      validateImage(conferenceInfoDataUI.Image);

    if (isValid && hasUnsavedChanges) {
      const response = await saveConferenceInfo(conferenceInfoDataUI);
      onConferenceInfoUpdate();
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
      console.log(response);
    } else {
      console.log("Data could not be submitted. Check validation.");
    }
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    switch (fieldName) {
      case "Name":
        setNameError(validateName(value) ? null : "Please enter a valid name");
        break;

      case "Phone":
        setPhoneError(
          validatePhone(value)
            ? null
            : "Please enter a valid phone number (e.g., 555-555-5555)"
        );
        break;

      case "Email":
        setEmailError(
          validateEmail(value) ? null : "Please enter a valid email address"
        );
        break;

      case "Address1":
        setAddress1Error(
          validateAddress1(value) ? null : "Please enter a valid address"
        );
        break;

      case "Address2":
        setAddress2Error(
          validateAddress2(value) ? null : "Please enter a valid address"
        );
        break;

      case "City":
        setCityError(validateCity(value) ? null : "Please enter a valid city");
        break;

      case "State":
        setStateError(
          validateState(value) ? null : "Please enter a valid state"
        );
        break;

      case "Zip":
        setZipError(
          validateZip(value) ? null : "Please enter a valid zip code"
        );
        break;

      case "Image":
        setImageError(
          validateImage(value) ? null : "Please select a valid image Path"
        );
        break;

      default:
        break;
    }
    setConferenceInfoDataUI((prevState:any) => ({
      ...prevState,
      [fieldName]: value,
    }));
  };
  /****************************************************************************************************/
  return (
    <div className="ConferenceDetailsParentStyle">
      <Box>
        {isShowAlert && (
          <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
            Conference Location successfully submitted.
          </Alert>
        )}
        <Typography variant="h6" component="h2" marginBottom={2}>
          Conference Location
        </Typography>
        <form onSubmit={handleSave}>
          <div>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              spacing={2}
            >
              <Grid item xs={12}>
                <div>
                  <TextField
                    label="Name"
                    fullWidth
                    margin="dense"
                    size="small"
                    id="Name"
                    value={conferenceInfoDataUI.Name ?? ""}
                    onChange={(e) => handleFieldChange("Name", e.target.value)}
                    helperText={
                      nameError ? (
                        <FormHelperText className="error-text">
                          {nameError}
                        </FormHelperText>
                      ) : (
                        ""
                      )
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <div>
                  <TextField
                    fullWidth
                    margin="dense"
                    size="small"
                    id="Phone Number"
                    label="Phone Number"
                    value={conferenceInfoDataUI.Phone ?? ""}
                    onChange={(e) => handleFieldChange("Phone", e.target.value)}
                    helperText={
                      phoneError ? (
                        <FormHelperText className="error-text">
                          {phoneError}
                        </FormHelperText>
                      ) : (
                        ""
                      )
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <div>
                  <TextField
                    fullWidth
                    margin="dense"
                    size="small"
                    id="Email Addresss"
                    label="Email Address"
                    value={conferenceInfoDataUI.Email ?? ""}
                    onChange={(e) => handleFieldChange("Email", e.target.value)}
                    helperText={
                      emailError ? (
                        <FormHelperText className="error-text">
                          {emailError}
                        </FormHelperText>
                      ) : (
                        ""
                      )
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <div>
                  <TextField
                    fullWidth
                    margin="dense"
                    size="small"
                    id="Address 1"
                    label="Address 1"
                    value={conferenceInfoDataUI.Address1 ?? ""}
                    onChange={(e) =>
                      handleFieldChange("Address1", e.target.value)
                    }
                    helperText={
                      address1Error ? (
                        <FormHelperText className="error-text">
                          {address1Error}
                        </FormHelperText>
                      ) : (
                        ""
                      )
                    }
                  />
                </div>
              </Grid>

              <Grid item xs={12}>
                <div>
                  <TextField
                    fullWidth
                    margin="dense"
                    size="small"
                    id="Address 2"
                    label="Address 2"
                    value={conferenceInfoDataUI.Address2 ?? ""}
                    onChange={(e) =>
                      handleFieldChange("Address2", e.target.value)
                    }
                    helperText={
                      address2Error ? (
                        <FormHelperText className="error-text">
                          {address2Error}
                        </FormHelperText>
                      ) : (
                        ""
                      )
                    }
                  />
                </div>
              </Grid>

              <Grid item xs={12}>
                <div>
                  <TextField
                    fullWidth
                    margin="dense"
                    size="small"
                    id="City"
                    label="City"
                    value={conferenceInfoDataUI.City ?? ""}
                    onChange={(e) => handleFieldChange("City", e.target.value)}
                    helperText={
                      cityError ? (
                        <FormHelperText className="error-text">
                          {cityError}
                        </FormHelperText>
                      ) : (
                        ""
                      )
                    }
                  />
                </div>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    margin="dense"
                    size="small"
                    id="State"
                    label="State"
                    value={conferenceInfoDataUI.State ?? ""}
                    onChange={(e) => handleFieldChange("State", e.target.value)}
                    error={!!stateError}
                    helperText={stateError || " "}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    margin="dense"
                    size="small"
                    id="Zip Code"
                    label="Zip Code"
                    value={conferenceInfoDataUI.Zip ?? ""}
                    onChange={(e) => handleFieldChange("Zip", e.target.value)}
                    error={!!zipError}
                    helperText={zipError || " "}
                  />

                </Grid>
              </Grid>
              <Grid item xs={12} className="image-center">
                {conferenceInfo?.Image ? (
                  <>
                    <div>
                      <img
                        src={conferenceInfo.Image}
                        alt="Image"
                        className="conference-image"
                      />
                    </div>
                    <div>
                      <div>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          id="Image"
                          label="Image"
                          value={conferenceInfoDataUI.Image}
                          onChange={(e) =>
                            handleFieldChange("Image", e.target.value)
                          }
                          helperText={
                            imageError ? (
                              <FormHelperText className="error-text">
                                {imageError}
                              </FormHelperText>
                            ) : (
                              ""
                            )
                          }
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    <TextField
                      fullWidth
                      margin="dense"
                      size="small"
                      id="Image"
                      label="Image"
                      defaultValue=""
                      onChange={(e) =>
                        handleFieldChange("Image", e.target.value)
                      }
                      helperText={
                        imageError ? (
                          <FormHelperText className="error-text">
                            {imageError}
                          </FormHelperText>
                        ) : (
                          ""
                        )
                      }
                    />
                  </div>
                )}
              </Grid>
            </Grid>
            <div
              className="SubmitButtonContainer"
            >
              <Button
                variant="contained"
                className="SaveButton"
                onClick={handleSave}
                disabled={!hasUnsavedChanges}
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      </Box>
    </div>
  );
};